import { Navigate, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Lock } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

/**
 * Gate de /studio. La audiencia la decide la pieza `studio` de profile.getVisibility (la misma
 * que muestra u oculta la pestaña del menú): funciones del Studio en el community map, o admin /
 * viewer del roster. Quien no la tiene recibe un mensaje explícito, nunca una página vacía; un
 * fallo al verificar se muestra como tal (no como "sin acceso").
 */
export function StudioProtectedRoute() {
  const { t } = useTranslation();
  const { data: visibility, isLoading, isError, refetch } = trpc.profile.getVisibility.useQuery(undefined, { staleTime: 5 * 60_000 });
  const access = visibility?.studio;
  // Si la query falló del todo (red, 500) tampoco es un "sin acceso": se muestra como degradado con Reintentar.
  const degraded = isError || !!access?.degraded || (!!visibility && visibility.degraded.includes('studio'));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <WinclapLoader size={48} className="text-foreground/25" />
      </div>
    );
  }
  if (!visibility && !isError) return <Navigate to="/dashboard" replace />;
  if (degraded) {
    return (
      <div className="max-w-xl mx-auto mt-16 px-4">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t('studio.denied.degraded_title', 'No pudimos verificar tu acceso')}</AlertTitle>
          <AlertDescription>
            {t('studio.denied.degraded_body', 'Falló la lectura del community map o de los permisos. No es un "sin acceso": reintentá en unos segundos y, si persiste, avisá a Internal.')}
            <div className="mt-3"><Button size="sm" variant="outline" onClick={() => refetch()}>{t('common.retry', 'Reintentar')}</Button></div>
          </AlertDescription>
        </Alert>
      </div>
    );
  }
  if (!access?.canView) {
    return (
      <div className="max-w-xl mx-auto mt-16 px-4">
        <Alert>
          <Lock className="h-4 w-4" />
          <AlertTitle>{t('studio.denied.title', 'Sin acceso a Winclap Studio')}</AlertTitle>
          <AlertDescription>
            {t('studio.denied.body', 'Este apartado es para el equipo del Studio (Content Partners). Si sos parte del Studio y no lo ves, pedile a Learning que te sume desde el panel.')}
          </AlertDescription>
        </Alert>
      </div>
    );
  }
  return <Outlet />;
}
